import React, { useState, useEffect } from 'react';
import { 
  FaFileCsv, 
  FaEdit, 
  FaTrash, 
  FaTimes, 
  FaSearch, 
  FaEye,
  FaDownload,
  FaFilter,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarker,
  FaVenusMars,
  FaBriefcase,
  FaBuilding,
  FaIdCard,
  FaLayerGroup,
  FaLock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCheck
} from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import axios from 'axios';

// =======================
// SAME MENU SECTIONS AS CreateEmployee
// =======================
const menuSections = [
  { name: "Home", pages: [{ name: "Dashboard", path: "/dashboard" }] },
  {
    name: "Doctor Category",
    pages: [
      { name: "Create Category", path: "/create-category" },
      { name: "Get All Doctor Categories", path: "/doctorcategorylist" }
    ]
  },
  {
    name: "Companies",
    pages: [
      { name: "Add Company", path: "/company-register" },
      { name: "Company List", path: "/companylist" }
    ]
  },
  {
    name: "Diagnostics",
    pages: [
      { name: "Add Diagnostics", path: "/create-diagnostic" },
      { name: "Diagnostics List", path: "/diagnosticlist" },
      { name: "Diagnostics Bookings", path: "/diagnosticslist" },
      { name: "Appointment Booking", path: "/book-diagnostic" }
    ]
  },
  {
    name: "Doctors",
    pages: [
      { name: "Add Doctor", path: "/create-doctor" },
      { name: "Doctor List", path: "/doctorlist" },
      { name: "Appointment List", path: "/appintmentlist" },
      { name: "Book An Appointment", path: "/appintmentbooking" }
    ]
  },
  {
    name: "Requests",
    pages: [
      { name: "Doctor Accepted Request", path: "/doctoracceptedlist" },
      { name: "Doctor Rejected Requests", path: "/doctorrejectedlist" },
      { name: "Diagnostic Accepted Request", path: "/diagnosticsacceptedlist" },
      { name: "Diagnostic Rejected Requests", path: "/diagnosticsrejectedlist" }
    ]
  },
  {
    name: "HRA",
    pages: [
      { name: "Add HRA Category", path: "/create-hracat" },
      { name: "HRA CategoryList", path: "/categorylist" },
      { name: "Add HRA", path: "/add-hra" },
      { name: "Get All HRA", path: "/hralist" },
      { name: "All HRA Submitted List", path: "/hrasubmissionlist" },
      { name: "Question", path: "/question" },
      { name: "User Answers", path: "/answer" }
    ]
  },
  {
    name: "Blog",
    pages: [
      { name: "Add Blogs", path: "/createblog" },
      { name: "Get All Blogs", path: "/bloglist" }
    ]
  },
  {
    name: "Test",
    pages: [
      { name: "Create Test", path: "/createtest" },
      { name: "Get All Tests", path: "/testlist" },
      { name: "Create Diagnostic Tests", path: "/creatediagtest" },
      { name: "Get All Diagnostic Tests", path: "/diagtestlist" }
    ]
  },
  {
    name: "Scan & X-Ray",
    pages: [
      { name: "Create X-Rays", path: "/createxray" },
      { name: "Get All X-Rays", path: "/xraylist" }
    ]
  },
  {
    name: "Packages",
    pages: [
      { name: "Create Package", path: "/create-package" },
      { name: "Get All Packages", path: "/package-list" }
    ]
  },
  {
    name: "Medical Uploads",
    pages: [{ name: "View Uploads", path: "/staffmedicaluplods" }]
  },
  {
    name: "Employee",
    pages: [
      { name: "Add Employee", path: "/create-employee" },
      { name: "Employee List", path: "/employeelist" }
    ]
  },
  {
    name: "Banners",
    pages: [{ name: "Add Banner", path: "/banner" }]
  },
  {
    name: "Customer Support Tickets",
    pages: [{ name: "All Tickets", path: "/raisedtickets" }]
  },
  {
    name: "Settings",
    pages: [{ name: "Settings", path: "/setting" }]
  }
];

// Helper: get page name from path
const getPageName = (path) => {
  for (const section of menuSections) {
    const found = section.pages.find(p => p.path === path);
    if (found) return found.name;
  }
  return path;
};

// Helper component for detail rows
const DetailItem = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-200">
    <span className="font-medium text-gray-600">{label}:</span>
    <span className="text-gray-800 font-semibold">{value || 'N/A'}</span>
  </div>
);

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filterRole, setFilterRole] = useState('All');
  const [filterDepartment, setFilterDepartment] = useState('All');
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [openSections, setOpenSections] = useState({});  // for edit modal

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Auto-hide alert
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const showAlert = (message, type = 'success') => setAlert({ message, type });

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://api.elthiumhealth.com/api/admin/getallemployees');
      setEmployees(res.data.employees || []);
      showAlert('Employees loaded successfully!', 'success');
    } catch (err) {
      console.error('Error fetching employees:', err);
      showAlert('Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  };

  const uniqueRoles = ['All', ...new Set(employees.map(emp => emp.role).filter(Boolean))];
  const uniqueDepartments = ['All', ...new Set(employees.map(emp => emp.department).filter(Boolean))];

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name?.toLowerCase().includes(search.toLowerCase()) ||
                          emp.email?.toLowerCase().includes(search.toLowerCase()) ||
                          emp.employeeId?.toLowerCase().includes(search.toLowerCase());
    const matchesRole = filterRole === 'All' || emp.role === filterRole;
    const matchesDepartment = filterDepartment === 'All' || emp.department === filterDepartment;
    return matchesSearch && matchesRole && matchesDepartment;
  });

  const handleEdit = (employee) => {
    setEditingEmployee({ ...employee });
    setSelectedPages(employee.pagesAccess || []);
    setOpenSections({});  // collapse all sections
    setIsModalOpen(true);
  };

  const handleView = (employee) => {
    setViewEmployee(employee);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await axios.delete(`https://api.elthiumhealth.com/api/admin/deleteemployee/${id}`);
      setEmployees(employees.filter(emp => emp._id !== id));
      showAlert('Employee deleted successfully!', 'success');
    } catch (error) {
      showAlert('Failed to delete employee', 'error');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.put(
        `https://api.elthiumhealth.com/api/admin/updateemployee/${editingEmployee._id}`,
        editingEmployee
      );
      setEmployees(employees.map(emp => emp._id === editingEmployee._id ? res.data.employee : emp));
      setIsModalOpen(false);
      showAlert('Employee updated successfully!', 'success');
    } catch (error) {
      showAlert('Failed to update employee', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle section in edit modal
  const toggleSection = (sectionName) => {
    setOpenSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  // Handle checkbox change for a page
  const handlePageCheckbox = (path, isChecked) => {
    let newSelectedPages;
    if (isChecked) {
      newSelectedPages = [...selectedPages, path];
    } else {
      newSelectedPages = selectedPages.filter(p => p !== path);
    }
    setSelectedPages(newSelectedPages);
    setEditingEmployee({ ...editingEmployee, pagesAccess: newSelectedPages });
  };

  // Select / deselect all pages
  const handleSelectAllPages = () => {
    const allPaths = menuSections.flatMap(s => s.pages.map(p => p.path));
    if (selectedPages.length === allPaths.length) {
      setSelectedPages([]);
      setEditingEmployee({ ...editingEmployee, pagesAccess: [] });
    } else {
      setSelectedPages(allPaths);
      setEditingEmployee({ ...editingEmployee, pagesAccess: allPaths });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      Active: 'bg-green-100 text-green-800',
      Inactive: 'bg-red-100 text-red-800',
      Pending: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // CSV headers (same as before)
  const headers = [
    { label: 'Employee ID', key: 'employeeId' },
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Role', key: 'role' },
    { label: 'Mobile', key: 'mobile' },
    { label: 'Address', key: 'address' },
    { label: 'Gender', key: 'gender' },
    { label: 'Designation', key: 'designation' },
    { label: 'Department', key: 'department' },
    { label: 'Grade', key: 'grade' },
    { label: 'Pages Access', key: 'pagesAccess' },
    { label: 'Created At', key: 'createdAt' },
  ];

  return (
    <>
      {/* Alert Toast */}
      {alert && (
        <div className={`fixed top-4 right-4 z-50 ${alert.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3`}>
          {alert.type === 'success' ? <FaCheck /> : <FaExclamationTriangle />}
          <span>{alert.message}</span>
          <button onClick={() => setAlert(null)} className="ml-4"><FaTimes /></button>
        </div>
      )}

      <div className="p-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <FaUser className="text-blue-600" />
                Employee Management
              </h1>
              <p className="text-gray-600 mt-2">Manage and track all employee information</p>
            </div>
            <div className="flex items-center gap-3">
              <CSVLink
                data={filteredEmployees}
                headers={headers}
                filename={`employees_${new Date().toISOString().split('T')[0]}.csv`}
                className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-300 flex items-center gap-2 font-medium shadow-md"
              >
                <FaDownload /> Export CSV
              </CSVLink>
              <button onClick={fetchEmployees} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium shadow-md">
                Refresh
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-200">
              <div className="flex justify-between items-center">
                <div><p className="text-gray-600 text-sm font-medium">Total Employees</p><p className="text-3xl font-bold text-gray-800 mt-2">{employees.length}</p></div>
                <div className="p-3 bg-blue-100 rounded-lg"><FaUser className="text-2xl text-blue-600" /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-200">
              <div className="flex justify-between items-center">
                <div><p className="text-gray-600 text-sm font-medium">Active</p><p className="text-3xl font-bold text-green-600 mt-2">{employees.filter(e => e.status === 'Active').length}</p></div>
                <div className="p-3 bg-green-100 rounded-lg"><FaCheckCircle className="text-2xl text-green-600" /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-200">
              <div className="flex justify-between items-center">
                <div><p className="text-gray-600 text-sm font-medium">Departments</p><p className="text-3xl font-bold text-purple-600 mt-2">{uniqueDepartments.length - 1}</p></div>
                <div className="p-3 bg-purple-100 rounded-lg"><FaBuilding className="text-2xl text-purple-600" /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md border border-gray-200">
              <div className="flex justify-between items-center">
                <div><p className="text-gray-600 text-sm font-medium">Filtered</p><p className="text-3xl font-bold text-orange-600 mt-2">{filteredEmployees.length}</p></div>
                <div className="p-3 bg-orange-100 rounded-lg"><FaFilter className="text-2xl text-orange-600" /></div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl p-6 shadow-md mb-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex-1 w-full relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input type="text" className="w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Search by name, email, or employee ID..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-4 py-3 border rounded-lg">
                {uniqueRoles.map(role => <option key={role} value={role}>{role}</option>)}
              </select>
              <select value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)} className="px-4 py-3 border rounded-lg">
                {uniqueDepartments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Employees Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div><p className="mt-4 text-gray-600">Loading employees...</p></div>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12"><div className="text-gray-400 text-6xl mb-4">👥</div><h3 className="text-xl font-semibold text-gray-700 mb-2">No employees found</h3><p className="text-gray-500">Try adjusting your search or filters</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Position</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Access</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEmployees.map(emp => (
                    <tr key={emp._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center"><FaUser className="h-6 w-6 text-blue-600" /></div>
                          <div className="ml-4"><div className="text-sm font-semibold text-gray-900">{emp.name}</div><div className="text-sm text-gray-500">ID: {emp.employeeId}</div><div className="text-xs text-gray-400">Joined: {formatDate(emp.createdAt)}</div></div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 flex items-center gap-2"><FaEnvelope className="text-gray-400" />{emp.email}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-2 mt-1"><FaPhone className="text-gray-400" />{emp.mobile || 'N/A'}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-2 mt-1"><FaMapMarker className="text-gray-400" /><span className="truncate max-w-xs">{emp.address || 'N/A'}</span></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-gray-900">{emp.designation}</div>
                          <div className="text-sm text-gray-600 flex items-center gap-2"><FaBuilding className="text-gray-400" />{emp.department}</div>
                          <div className="text-sm text-gray-600 flex items-center gap-2"><FaVenusMars className="text-gray-400" />{emp.gender}</div>
                          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(emp.status || 'Active')}`}>{emp.status || 'Active'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center gap-2 mb-1"><FaLock className="text-gray-400" /><span className="font-medium">Pages Access:</span></div>
                          {emp.pagesAccess && emp.pagesAccess.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {emp.pagesAccess.slice(0, 3).map((page, idx) => (
                                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">{getPageName(page).split(' ')[0]}</span>
                              ))}
                              {emp.pagesAccess.length > 3 && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">+{emp.pagesAccess.length - 3} more</span>}
                            </div>
                          ) : <span className="text-gray-400 text-sm">No access assigned</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleView(emp)} className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg"><FaEye /></button>
                          <button onClick={() => handleEdit(emp)} className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg"><FaEdit /></button>
                          <button onClick={() => handleDelete(emp._id)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"><FaTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filteredEmployees.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-700">Showing {filteredEmployees.length} of {employees.length} employees</div>
            </div>
          )}
        </div>
      </div>

      {/* ========== EDIT MODAL (Hierarchical Page Access) ========== */}
      {isModalOpen && editingEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="bg-blue-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3"><FaEdit className="text-white text-xl" /><h2 className="text-xl font-bold text-white">Edit Employee Details</h2></div>
                <button onClick={() => setIsModalOpen(false)} className="text-white hover:bg-white/20 p-2 rounded-full"><FaTimes className="text-xl" /></button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[calc(85vh-120px)] p-6">
              <form onSubmit={handleUpdate}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label><input type="text" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.name || ''} onChange={e => setEditingEmployee({...editingEmployee, name: e.target.value})} required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Employee ID *</label><input type="text" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.employeeId || ''} onChange={e => setEditingEmployee({...editingEmployee, employeeId: e.target.value})} required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Gender</label><select className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.gender || ''} onChange={e => setEditingEmployee({...editingEmployee, gender: e.target.value})}><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option></select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Email *</label><input type="email" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.email || ''} onChange={e => setEditingEmployee({...editingEmployee, email: e.target.value})} required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Mobile *</label><input type="tel" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.mobile || ''} onChange={e => setEditingEmployee({...editingEmployee, mobile: e.target.value})} required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Password</label><input type="password" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.password || ''} onChange={e => setEditingEmployee({...editingEmployee, password: e.target.value})} placeholder="Leave blank to keep current" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Designation *</label><input type="text" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.designation || ''} onChange={e => setEditingEmployee({...editingEmployee, designation: e.target.value})} required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Department *</label><input type="text" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.department || ''} onChange={e => setEditingEmployee({...editingEmployee, department: e.target.value})} required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Grade</label><input type="text" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.grade || ''} onChange={e => setEditingEmployee({...editingEmployee, grade: e.target.value})} /></div>
                </div>
                <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-2">Address</label><textarea rows="3" className="w-full px-4 py-2 border rounded-lg" value={editingEmployee.address || ''} onChange={e => setEditingEmployee({...editingEmployee, address: e.target.value})} /></div>

                {/* Hierarchical Pages Access */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pages Access</label>
                  <div className="border border-gray-300 rounded-md p-3 max-h-80 overflow-y-auto">
                    {menuSections.map((section, idx) => (
                      <div key={idx} className="mb-2">
                        <button type="button" className="flex justify-between items-center w-full text-left font-semibold text-gray-800 bg-gray-100 px-3 py-2 rounded-md hover:bg-gray-200" onClick={() => toggleSection(section.name)}>
                          <span>{section.name}</span>
                          <span>{openSections[section.name] ? "▼" : "▶"}</span>
                        </button>
                        {openSections[section.name] && (
                          <div className="ml-4 mt-1 space-y-1">
                            {section.pages.map((page, pIdx) => (
                              <label key={pIdx} className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                                <input type="checkbox" className="rounded border-gray-300" checked={selectedPages.includes(page.path)} onChange={(e) => handlePageCheckbox(page.path, e.target.checked)} />
                                <span>{page.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <button type="button" onClick={handleSelectAllPages} className="text-sm text-blue-600 hover:text-blue-800">
                      {selectedPages.length === menuSections.flatMap(s => s.pages.map(p => p.path)).length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="text-sm text-gray-600 ml-4">Selected: {selectedPages.length} pages</span>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 ${isSubmitting ? 'opacity-50' : ''}`}>{isSubmitting ? 'Updating...' : 'Update Employee'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ========== VIEW MODAL (Grouped Pages by Category) ========== */}
      {isViewModalOpen && viewEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <div className="bg-green-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3"><FaEye className="text-white text-xl" /><h2 className="text-xl font-bold text-white">Employee Details</h2></div>
                <button onClick={() => setIsViewModalOpen(false)} className="text-white hover:bg-white/20 p-2 rounded-full"><FaTimes className="text-xl" /></button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[calc(85vh-120px)] p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg text-gray-800 mb-3">Personal Information</h3>
                  <DetailItem label="Full Name" value={viewEmployee.name} />
                  <DetailItem label="Employee ID" value={viewEmployee.employeeId} />
                  <DetailItem label="Gender" value={viewEmployee.gender} />
                  <DetailItem label="Email" value={viewEmployee.email} />
                  <DetailItem label="Mobile" value={viewEmployee.mobile} />
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg text-gray-800 mb-3">Professional Information</h3>
                  <DetailItem label="Designation" value={viewEmployee.designation} />
                  <DetailItem label="Department" value={viewEmployee.department} />
                  <DetailItem label="Grade" value={viewEmployee.grade} />
                  <DetailItem label="Role" value={viewEmployee.role} />
                  <DetailItem label="Status" value={viewEmployee.status || 'Active'} />
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-lg text-gray-800 mb-2">Address</h3>
                <p className="text-gray-700">{viewEmployee.address || 'No address provided'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-800 mb-2">Pages Access</h3>
                <div className="space-y-3">
                  {Object.entries(
                    viewEmployee.pagesAccess?.reduce((acc, path) => {
                      for (const sec of menuSections) {
                        const found = sec.pages.find(p => p.path === path);
                        if (found) {
                          if (!acc[sec.name]) acc[sec.name] = [];
                          acc[sec.name].push(found.name);
                          break;
                        }
                      }
                      return acc;
                    }, {}) || {}
                  ).map(([category, pages]) => (
                    <div key={category}>
                      <div className="font-medium text-gray-800">{category}</div>
                      <div className="flex flex-wrap gap-2 mt-1 ml-4">
                        {pages.map((name, idx) => <span key={idx} className="px-2 py-1 bg-white border text-sm rounded">{name}</span>)}
                      </div>
                    </div>
                  ))}
                  {(!viewEmployee.pagesAccess || viewEmployee.pagesAccess.length === 0) && <p className="text-gray-500">No pages access assigned</p>}
                </div>
              </div>
            </div>
            <div className="bg-gray-100 px-6 py-4 border-t">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">Created: {formatDate(viewEmployee.createdAt)}</div>
                <div className="flex gap-3">
                  <button onClick={() => { setIsViewModalOpen(false); handleEdit(viewEmployee); }} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Edit Employee</button>
                  <button onClick={() => setIsViewModalOpen(false)} className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeList;